import { UserTypes } from '@api';
import {
    getIsRunningOnDevelopersMachine, PropertyTypesOf
} from '@utils';

type Config = {
    applicationName: string
    defaultEnv: string
    isRunningOnDevelopersMachine: boolean
    shouldLoadLocaleFromCDN: boolean
    configurationGateway: Record<string, string>
    authenticationGateway: Record<string, string>
    jsonConfigKeys: Array<AppConfigurationKeys>
    defaultIdleTimeoutMins: number
    defaultCountdownTimeoutInMins: number
    defaultLocale: string
    emailVerificationFeature: boolean
};

// The 3p config application name
const applicationName = 'PortalHost';
// This env is required for development only login / token handling in the
// url params. In production its not used and all these urls are derived from remote config.
const params = new URLSearchParams(location.search.replace('?', ''));
const defaultEnv = params.get('env') || 'qa';

export const APP_CONFIG: Config = {
    applicationName,
    isRunningOnDevelopersMachine: getIsRunningOnDevelopersMachine(),
    defaultEnv,
    // For development this configuration is hard-coded,
    // the C# Host application provides this as a window global pppConfig.
    configurationGateway: {
        demo: `//demo.3plearning.com/ConfigurationGateway/v2/applications/${applicationName}/configurations`,
        qa: `//config-qa.3plearning.com/ConfigurationGateway/v2/applications/${applicationName}/configurations`,
        live: `//config.3plearning.com/ConfigurationGateway/v2/applications/${applicationName}/configurations`
    },
    authenticationGateway: {
        demo: 'https://demo.3plearning.com/AuthenticationGateway/api/sessions',
        qa: 'https://gateway-qa.3plearning.com/AuthenticationGatewayV1/api/sessions',
        live: 'https://identity-gateway.3plearning.com/AuthenticationGatewayV1/api/sessions'
    },
    // Listing keys here allows them to be automatically parsed as JSON,
    // After adding them, define their types in the JSONConfigValues type below.
    jsonConfigKeys: [
        'LoginURL',
        'ShowProductSelector'
    ],
    defaultIdleTimeoutMins: 5,
    defaultCountdownTimeoutInMins: 5,
    shouldLoadLocaleFromCDN: true,
    // TODO discuss using proper browser default with navigator.language.
    // This would require the deploy of 'en' not just the 'en-AU' for browser english fallback.
    // https://developer.mozilla.org/en-US/docs/Web/API/Navigator/language
    defaultLocale: 'en-AU',
    // Waiting for BE to be implemented for emailVerificationFeature
    emailVerificationFeature: false
};

// TODO this is just taken from current QA env, need to confirm whats actually needed?
export const ConfigurationKeys = {
    AssignReviewPromoVideoUrl: 'AssignReviewPromoVideoUrl',
    AuthenticationGateway: 'AuthenticationGateway',
    AuthenticationServicePathUrl: 'AuthenticationServicePathUrl',
    COMBINED_ASSIGN_MODULE_ID: 'COMBINED_ASSIGN_MODULE_ID',
    DUCSAuthenticationGatewayEndpoint: 'DUCSAuthenticationGatewayEndpoint',
    DUCSCdnBasePath: 'DUCSCdnBasePath',
    DUCSCdnUrl: 'DUCSCdnUrl',
    DUCSContentApiEndpoint: 'DUCSContentApiEndpoint',
    DUCSContentGatewayEndpoint: 'DUCSContentGatewayEndpoint',
    DUCSLibraryUrl: 'DUCSLibraryUrl',
    DUCSPagesCdnDirectoryPath: 'DUCSPagesCdnDirectoryPath',
    FeatureEmailVerificationModal: 'FeatureEmailVerificationModal',
    FeedbackUrl: 'FeedbackUrl',
    FlashInterceptorWalkMeUrl: 'FlashInterceptorWalkMeUrl',
    FlexMathleticsBaseUrl: 'FlexMathleticsBaseUrl',
    GoogleTagmanagerID: 'GoogleTagmanagerID',
    HelpUrl: 'HelpUrl',
    IndexHtmlFileUrl: 'IndexHtmlFileUrl',
    LocalisationCdnBaseUrl: 'LocalisationCdnBaseUrl',
    LoginURL: 'LoginURL',
    ModuleIdsForNewBadgeStyling: 'ModuleIdsForNewBadgeStyling',
    ModuleVideos: 'ModuleVideos',
    NeedHelpUrl: 'NeedHelpUrl',
    NewCoursesPromoVideoUrl: 'NewCoursesPromoVideoUrl',
    NewProductSelector: 'NewProductSelector',
    OldMathleticsUrl: 'OldMathleticsUrl',
    ProductSelectorGateway: 'ProductSelectorGateway',
    PromoVideoUrl: 'PromoVideoUrl',
    SchoolRollGatewayUrl: 'SchoolRollGatewayUrl',
    ShowProductSelector: 'ShowProductSelector',
    ShowVersionSelector: 'ShowVersionSelector',
    StudentViewBaseUrl: 'StudentViewBaseUrl',
    TracesSampleRate: 'TracesSampleRate',
    UserPortalGateway: 'UserPortalGateway',
    WelcomeVideo: 'WelcomeVideo',
    countdownTimeoutInMinutes: 'countdownTimeoutInMinutes',
    env: 'env',
    idleTimeoutInMinutes: 'idleTimeoutInMinutes',
    isInterceptorOn: 'isInterceptorOn',
    numeracyForEarlyLearnersModuleId: 'numeracyForEarlyLearnersModuleId',
    productInterceptor: 'productInterceptor',
    promoVideoCountryCodes: 'promoVideoCountryCodes',
    promoVideoValidTeacherYearGroups: 'promoVideoValidTeacherYearGroups',
    sessionBufferTimeInSeconds: 'sessionBufferTimeInSeconds',
    sessionExpiryTimeInMinutes: 'sessionExpiryTimeInMinutes',
    sessionOverrideTimeInMinutes: 'sessionOverrideTimeInMinutes',
    sessionThrottleTimeInMinutes: 'sessionThrottleTimeInMinutes',
    sessionTimeOutInMinutes: 'sessionTimeOutInMinutes',
    showAssignAndReviewButton: 'showAssignAndReviewButton',
    showPromoVideoButton: 'showPromoVideoButton',
    showPromoVideoModal: 'showPromoVideoModal',
    tryNewCourseModuleIds: 'tryNewCourseModuleIds',
    showProductSwitcher: 'showProductSwitcher',
    THREE_ESSENTIALS_URL: 'THREE_ESSENTIALS_URL',
    THREE_ESSENTIALS_LOGOUT_QUERY_PARAM_KEY: 'THREE_ESSENTIALS_LOGOUT_QUERY_PARAM_KEY',
    READING_URL: 'READING_URL',
    WRITING_URL: 'WRITING_URL',
    MATHEMATICS_URL: 'MATHEMATICS_URL',
    THREE_ESSENTIALS_EDIT_MY_PROFILE_URL: 'THREE_ESSENTIALS_EDIT_MY_PROFILE_URL',
    SignupForFreeTrial: 'SignupForFreeTrial',
    rolloverModuleId: 'rolloverModuleId',
    mxAPACCountryCodes: 'mxAPACCountryCodes',
    PromoBanner: 'PromoBanner'
};

/**
 * This type allows for manually strictly typing the parsed json values.
 * First define the key in the main Config.jsonConfigKeys.
 * Then add the keys to JSONConfigValues as below.
 */
type JSONConfigValues = {
    LoginURL: LoginURLConfigJSON
    ShowProductSelector: ShowProductSelectorJSON
};

/**
 * Combined type for application configuration.
 * This includes overrides in the Exclude<> for parsed JSON data types.
 */
export type ConfigurationsData =
    & Exclude<ConfigurationKeyedData, keyof JSONConfigValues>
    & JSONConfigValues;

export type AppConfigurationKeys = PropertyTypesOf<typeof ConfigurationKeys>;

export type ConfigurationKeyedData = {
    [Key in AppConfigurationKeys]: string | null
};

/**
 * Used for redirects with the 3p sign-in app. The urls can be product specific
 * and are used for logout redirection see selectTrySigninUrl().
 * Example json '[{"productId": 1, "signinUrl": "https://login-qa.mathletics.com"}]'.
 */
export type LoginURLConfigJSON = Array<{
    productId: number
    signinUrl: string
}>;

/**
 * Used to allow user types to show the product selector.
 * See the business rule in shouldShowProductSelector().
 */
export type ShowProductSelectorJSON = {
    userProfileType: Array<UserTypes>
};

/**
 * Required props for init of ducs container.
 */
export type DucsInitConfig = {
    DUCSAuthenticationGatewayEndpoint: string
    DUCSCdnUrl: string
    DUCSContentApiEndpoint: string
    DUCSLibraryUrl: string
};
